import { useTranslation } from "react-i18next";
import { TopList } from "./TopList";
import { type GpuStatsQuery, type TopDeployment } from "../api";
import { SectionCard } from "./SectionCard";
import { formatCurrency, formatNumber } from "../utils";

type Props = {
  gpu: GpuStatsQuery;
  limit: number;
  onLimitChange: (limit: number) => void;
};

const formatGpuHours = (value: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);

export function TopDeployments({ gpu, limit, onLimitChange }: Props) {
  const { t } = useTranslation();
  if (!gpu.isPending && (gpu.isError || !gpu.data?.usage_available || !gpu.data.usage)) {
    return (
      <SectionCard title={t("usageAndCost.topDeployments.title")} category={t("usageAndCost.topDeployments.category")}>
        <p className="py-6 text-center text-sm text-muted-foreground">{t("usageAndCost.gpuUnavailable")}</p>
        <button className="text-sm underline" onClick={() => gpu.refetch()}>{t("common.retry")}</button>
      </SectionCard>
    );
  }
  const rows = gpu.data?.usage?.deployments;


  return (
    <TopList<TopDeployment>
      title={t("usageAndCost.topDeployments.title")}
      category={t("usageAndCost.topDeployments.category")}
      useData={() => ({ data: rows, isLoading: gpu.isPending })}
      size={limit}
      onSizeChange={onLimitChange}
      rowKey={(row) => row.deployment_id}
      chartKey="gpu_hours"
      chartLabelKey="deployment_name"
      chartLabelFormatter={(value) =>
        `${formatGpuHours(value)} ${t("usageAndCost.topDeployments.hours")}`
      }
      columns={[
        {
          key: "deployment_name",
          header: t("usageAndCost.topDeployments.deploymentName"),
          accessor: (row) => (
            <span className="block max-w-[260px] truncate">
              {row.deployment_name}
            </span>
          ),
        },
        {
          key: "total_requests",
          header: t("usageAndCost.topDeployments.totalRequests"),
          align: "end",
          accessor: (row) => (
            <span className="text-muted-foreground">
              {formatNumber(row.total_requests)}
            </span>
          ),
        },
        {
          key: "gpu_hours",
          header: t("usageAndCost.topDeployments.gpuHours"),
          align: "end",
          accessor: (row) =>
            `${formatGpuHours(row.gpu_hours)} ${t("usageAndCost.topDeployments.hours")}`,
        },
        {
          key: "total_spend",
          header: t("usageAndCost.topDeployments.spendUsd"),
          align: "end",
          accessor: (row) => (
            <span className="text-muted-foreground">
              {formatCurrency(row.total_spend)}
            </span>
          ),
        },
      ]}
    />
  );
}
